import { api } from "@/lib/api";

export type NositeljObveze = "company" | "equipment_object" | "site";
export type VrstaSubjektaObveze = "company" | "site";
export type PeriodikaObveze =
  | "one_time"
  | "periodic"
  | "continuous"
  | "event_driven";
export type StatusRokaObveze =
  | "compliant"
  | "warning"
  | "expired"
  | "no_deadline";

export interface ObvezaTvrtke {
  id: string;
  company_obligation_type_id: string;
  subject_type: VrstaSubjektaObveze;
  occurrence_id: string | null;
  code: string;
  name_hr: string;
  scope: NositeljObveze;
  holder_name: string;
  gradiliste_id: string | null;
  legal_source: string;
  legal_article: string | null;
  frequency: PeriodikaObveze;
  interval_days: number | null;
  periodicity_note: string | null;
  interval_min_days: number | null;
  interval_max_days: number | null;
  employer_penalty_eur: string | null;
  responsible_person_penalty_eur: string | null;
  penalty_note: string | null;
  consequence: string;
  fulfilment_status:
    | "not_proven"
    | "proven"
    | "expires"
    | "expired"
    | "not_applicable";
  deadline_status: StatusRokaObveze;
  last_completed_on: string | null;
  next_due_on: string | null;
  responsible_person: string | null;
  responsible_user_id?: string | null;
  responsible_user_name?: string | null;
  instruction_hr?: string | null;
  evidence_hint?: string | null;
  note: string | null;
  interval_days_override: number | null;
  evidence_count: number;
  latest_evidence_document_id: string | null;
}

export interface IzmjenaObveze {
  responsible_person?: string | null;
  responsible_user_id?: string | null;
  note?: string | null;
  interval_days_override?: number | null;
  next_due_on?: string | null;
}

export interface ObligationAssignee { id: string; full_name: string }
export interface ObligationType {
  id: string; code: string; name_hr: string; scope: NositeljObveze;
  frequency: PeriodikaObveze; status: "active" | "draft_legal_review" | "retired";
  already_registered: boolean;
}
export interface ObligationSite { id: string; naziv: string; available_type_count: number }
export interface NewObligation {
  company_obligation_type_id: string; gradiliste_id: string | null; activated_on: string;
  next_due_on: string | null; responsible_user_id: string | null; note: string | null;
}
export async function getObligationTypes(gradilisteId?: string | null, signal?: AbortSignal): Promise<ObligationType[]> {
  return (await api.get<ObligationType[]>("/company-obligations/types", { signal, ...(gradilisteId ? { params: { gradiliste_id: gradilisteId } } : {}) })).data;
}
export async function getObligationSites(signal?: AbortSignal): Promise<ObligationSite[]> {
  return (await api.get<ObligationSite[]>("/company-obligations/sites", { signal })).data;
}
export async function createObligation(payload: NewObligation): Promise<ObvezaTvrtke> {
  return (await api.post<ObvezaTvrtke>("/company-obligations", payload)).data;
}
export interface ObligationOccurrence {
  id: string; due_on: string | null; completed_on: string | null;
  completed_by_id: string | null; completed_by_name: string | null;
  completion_note: string | null; fulfilment_status: string;
}
export interface ObligationEvidence {
  id: string; dokument_id: string; archive_id: string | null; naziv: string; verified_by_id: string | null;
  verified_at: string | null; occurrence_id: string | null;
}
export interface ObligationDetail {
  occurrences: ObligationOccurrence[]; evidence: ObligationEvidence[];
}
export async function getObligationAssignees(signal?: AbortSignal): Promise<ObligationAssignee[]> {
  return (await api.get<ObligationAssignee[]>("/company-obligations/assignees", { signal })).data;
}
export async function getObligationDetail(obligation: ObvezaTvrtke, signal?: AbortSignal): Promise<ObligationDetail> {
  const kind = obligation.subject_type === "company" ? "instances" : "site-instances";
  return (await api.get<ObligationDetail>(`/company-obligations/${kind}/${obligation.id}/detail`, { signal })).data;
}
export async function updateObligationInstructions(typeId: string, payload: { instruction_hr: string | null; evidence_hint: string | null }): Promise<void> {
  await api.patch(`/company-obligations/types/${typeId}/instructions`, payload);
}
export async function getObligationSummary(signal?: AbortSignal): Promise<{ expired: number; my_open_tasks: number }> {
  return (await api.get<{ expired: number; my_open_tasks: number }>("/company-obligations/summary", { signal })).data;
}
export function notifyObligationsChanged(): void {
  window.dispatchEvent(new Event("obligations-changed"));
}

export interface IzvrsenjeObveze {
  completed_on: string;
  dokument_id: string;
  note: string | null;
}

export async function dohvatiObvezeTvrtke(
  signal?: AbortSignal,
): Promise<ObvezaTvrtke[]> {
  const response = await api.get<ObvezaTvrtke[]>(
    "/company-obligations/register",
    { signal },
  );
  return response.data;
}

export async function izmijeniObvezu(
  obveza: ObvezaTvrtke,
  payload: IzmjenaObveze,
): Promise<void> {
  const putanja =
    obveza.subject_type === "company"
      ? `/company-obligations/instances/${obveza.id}`
      : `/company-obligations/site-instances/${obveza.id}`;
  await api.patch(putanja, payload);
}

export async function oznaciObvezuIzvrsenom(
  obveza: ObvezaTvrtke,
  payload: IzvrsenjeObveze,
): Promise<void> {
  let putanja: string;
  if (obveza.occurrence_id) {
    putanja = `/company-obligations/occurrences/${obveza.occurrence_id}/complete`;
  } else if (obveza.subject_type === "company") {
    putanja = `/company-obligations/instances/${obveza.id}/complete`;
  } else {
    putanja = `/company-obligations/site-instances/${obveza.id}/complete`;
  }
  await api.post(putanja, payload);
}
